import { defineStore } from 'pinia';
import { fetchEndpoint } from '@/helpers/storeFunctions.js';
import { reactive } from 'vue';
import { useUserStore } from '@/stores/userStore.js';
import { hasUserDataSubmission } from '@/helpers/steps.js';

export const useApplicationStore = defineStore('applicationStore', {
    state: () =>
        reactive({
            executionStatus: null,
            application: {
                id: -1,
                referral: null,
                active: null,
                preferred_ae_id_c: null,
                contract_type_id: null,
                workflow: null,
                workflow_step: null,
                environment: null,
                purchasing_stage: 'in30Days',
                connected_with_ae: null,
                preferred_account_executive_id: 0,
                have_you_found_home: null,
                address: {
                    line1: '',
                    city: '',
                    state: '',
                    postal_code: '',
                    county: '',
                    latitude: '',
                    longitude: '',
                    search_address: ''
                },
                rebate: null,
                property_type: null,
                occupancy_type: null,
                sales_price: null,
                starting_price: null,
                first_time_homebuyer: null,
                down_payment: null,
                down_payment_assistance: null,
                working_with_agent: null,
                connect_with_buyers_agent: null,
                real_estate_agent: {
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone_number: ''
                },
                own_or_rent: null,
                current_address: {
                    line1: '',
                    city: '',
                    state: '',
                    postal_code: '',
                    county: '',
                    latitude: '',
                    longitude: '',
                    search_address: ''
                },
                sell_current_home: null,
                appraisal_price: null,
                expense_reo_monthly: null,
                expense_rent_monthly: null,
                current_balance: null,
                buying_with_ghs: null,
                selling_with_ghs: null,
                connect_with_sellers_agent: null,
                first_name: '',
                last_name: '',
                full_name: '',
                email: '',
                phone_number: '',
                marital_status: '',
                sms_opt_in: true,
                sell_save_agree_to_conditions: true,
                co_applicant_applying: null,
                co_applicant: {
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone_number: ''
                },
                credit_score: null,
                employment_status: null,
                has_bankruptcy: null,
                income_employment_yearly: null,
                income_social_security_monthly: null,
                income_rent_monthly: null,
                income_other_monthly: null,
                income_child_support_monthly: null,
                income_alimony_monthly: null,
                income_self_employment_monthly: null,
                co_applicant_income_employment_yearly: null,
                expense_credit_card_monthly: null,
                expense_installment_monthly: null,
                expense_alimony_monthly: null,
                contract_amount: null,
                ssn: null,
                goal_faith_based: true,
                goal_cash_out: false,
                goal_payment_reduction: false,
                goal_change_term: false,
                refinance_goal: 'goal_payment_reduction',
                plan_to_use_cash_consolidate_debt: null,
                plan_to_use_cash_home_improvements: null,
                plan_to_use_cash_buy_property: null,
                plan_to_use_cash_other: null,
                cash_out: null,
                pay_hoa_dues: false,
                hoa_dues: null,
                property_tax_and_hoa_included: null,
                property_tax: null,
                homeowners_insurance: null,
                seller_concessions: null,
                made_earnest_money_deposit: null,
                earnest_money_deposit: null,
                seller_covering_any_cost: 0,
                seller_cover_amount: null,
                estimated_closing_date: '',
                selling_state: '',
                approved: false,
                steps_done: null,
                total_steps: null,
                no_additional_monthly_income: false,
                has_credit_card_expense: false,
                has_installment_expense: false,
                has_child_support_expense: false,
                captchaVerified: false,
                is_phone_verified: false,
                phone_verification_status: null,

                // new fields
                working_with_buy_agent: null,
                working_with_sell_agent: null,
                buy_agent: {
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone_number: ''
                },
                sell_agent: {
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone_number: ''
                },
                use_same_agent: null
            }
        }),

    actions: {
        async sendPrequalLetter() {
            try {
                if (!this.application.id || this.application.id === -1) {
                    throw new Error('Application ID is missing.');
                }

                return await fetchEndpoint(
                    this,
                    'api/quick-prequals/send-prequal-letter',
                    'post',
                    { quick_prequal_id: this.application.id },
                    'executionStatus'
                );
            } catch (error) {
                console.error('Error sending prequal letter:', error);
                return error;
            }
        },

        async downloadPrequalLetter() {
            if (this.application.id === -1) {
                throw new Error('Application ID is missing.');
            }

            const token = localStorage.getItem('access_token');

            return await fetch('/my/pre-qualify/api/quick-prequals/download-prequal-letter', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ quick_prequal_id: this.application.id })
            });
        },

        async update(stepData, token = null, skipApiCalls = false) {
            try {
                let feedback = null;
                const method = this.application.id === -1 ? 'post' : 'patch';
                const endpoint =
                    this.application.id === -1
                        ? 'api/quick-prequals'
                        : `api/quick-prequals/${this.application.id}`;

                const excludedFields = ['executionStatus']; // Fields to exclude from the application data
                const excludedFieldValues = [null]; // Field values to exclude from the application data

                // Filter application data to send only necessary fields
                const filteredData = stepData.reduce((acc, field) => {
                    if (
                        field in this.application &&
                        !excludedFields.includes(field) &&
                        (!excludedFieldValues.includes(this.application[field]) ||
                            field === 'preferred_account_executive_id')
                    ) {
                        acc[field] = this.application[field];
                    }
                    return acc;
                }, {});

                // Ensure at least ID is sent if updating
                if (parseInt(this.application.id)) {
                    filteredData.id = this.application.id;
                }

                if (token !== null) {
                    filteredData.recaptcha_token = token;
                }

                if (skipApiCalls) {
                    filteredData.skip_api_calls = skipApiCalls;
                }

                if (hasUserDataSubmission(this.application?.workflow_step)) {
                    filteredData.client_fingerprint = localStorage.test_token ||= (
                        Math.random().toString(36) + Date.now().toString(36)
                    ).slice(2);
                }

                // console.warn('Data sent to backend: ', filteredData);

                const response = await fetchEndpoint(
                    this,
                    endpoint,
                    method,
                    filteredData,
                    'executionStatus'
                );

                // Save the application ID if it was created
                if (response.status_code === 200 && parseInt(response.data.id)) {
                    this.application.id = parseInt(response.data.id);

                    // we need to send the Google Analytics event on the steps where we have user data submission
                    if (hasUserDataSubmission(response.data.workflow_step)) {
                        this.sendAnalyticsEvent();
                    }
                }

                if (response && response.data) {
                    this.application.steps_done = response.data.steps_done;
                    this.application.total_steps = response.data.total_steps;
                    this.application.approved = response.data.approved;
                    this.application.contract_amount = response.data.contract_amount;
                    this.application.active = response.data.active;
                    this.application.is_phone_verified = response.data.is_phone_verified;
                    this.application.phone_verification_status =
                        response.data.phone_verification_status;

                    // Recursively update only if value is different and not null/undefined or 0
                    updateNestedObject(this.application, response.data);
                }

                feedback = response;
                return feedback;
            } catch (error) {
                // console.error('Error in update (save application):', error);
                return error;
            }
        },

        async getApplication(id) {
            try {
                const response = await fetchEndpoint(
                    this,
                    `api/quick-prequals/${id}`,
                    'get',
                    null,
                    'application'
                );

                const { data } = response;

                return data;
            } catch (error) {
                return error;
            }
        },

        async delete(id) {
            try {
                await fetchEndpoint(this, `api/quick-prequals/${id}`, 'delete', null, null);

                const userStore = useUserStore();
                await userStore.removeApplicationFromList(id);
            } catch (error) {
                return error;
            }
        },

        saveToLocalStorage() {
            try {
                const parsedData = parseFormData(this.$state);
                localStorage.setItem('applicationStore', JSON.stringify(parsedData));
            } catch (error) {
                console.error('Error saving to localStorage:', error);
            }
        },

        loadFromLocalStorage() {
            try {
                const savedState = localStorage.getItem('applicationStore');
                if (savedState) {
                    this.$patch(parseFormData(JSON.parse(savedState)));
                }
            } catch (error) {
                console.error('Error loading from localStorage:', error);
            }
        },

        clearStore() {
            localStorage.removeItem('applicationStore');

            this.$reset();
        },

        sendAnalyticsEvent() {
            if (typeof window !== 'undefined' && window.dataLayer) {
                window.dataLayer.push({
                    event: 'ec_form_submit',
                    user_data: {
                        email: this.application.email,
                        phone_number: '+' + this.application.phone_number
                    }
                });
            }
        }
    }
});

/**
 * Ensures that stored values are correctly typed.
 */
export function parseFormData(data) {
    if (typeof data !== 'object' || data === null) return data;

    return Object.keys(data).reduce((acc, key) => {
        let value = data[key];

        if (Array.isArray(value)) {
            acc[key] = value.map((item) => parseFormData(item)); // Ensure arrays are preserved
        } else if (typeof value === 'object' && value !== null) {
            acc[key] = parseFormData(value); // Recursively parse nested objects
        } else if (value === 'true') {
            acc[key] = true;
        } else if (value === 'false') {
            acc[key] = false;
        } else if (value === 'null' || value === null) {
            acc[key] = null;
        } else if (value !== '' && !isNaN(value) && typeof value !== 'boolean') {
            acc[key] = Number(value);
        } else {
            acc[key] = value;
        }

        return acc;
    }, {});
}

/**
 * Recursively updates nested objects & properly merges arrays.
 */
function updateNestedObject(target, source) {
    Object.keys(source).forEach((key) => {
        if (Array.isArray(source[key])) {
            target[key] = mergeArrays(target[key] || [], source[key]); // Merge arrays
        } else if (typeof source[key] === 'object' && source[key] !== null) {
            if (!target[key] || typeof target[key] !== 'object') {
                target[key] = {};
            }
            updateNestedObject(target[key], source[key]); // Recursively update nested objects
        } else if (JSON.stringify(target[key]) !== JSON.stringify(source[key])) {
            target[key] = source[key]; // Update only if changed
            // console.warn(`Updated ${key} with value:`, source[key]);
        }
    });
}

/**
 * Merges arrays, updating existing items and adding new ones.
 */
function mergeArrays(existingArray, newArray) {
    // Directly return the newArray to overwrite the existing gifts
    return newArray.filter((item) => item !== null && item !== undefined && item !== 0);
}
